import api from './api';

/**
 * Safely read a backend error message when Axios is configured
 * with responseType: "blob".
 */
async function getBlobErrorMessage(error) {
  const fallbackMessage = 'Failed to download waybill';

  try {
    const responseData = error?.response?.data;

    if (responseData instanceof Blob) {
      const text = await responseData.text();

      if (!text) {
        return fallbackMessage;
      }

      try {
        const parsed = JSON.parse(text);

        return (
          parsed.message ||
          parsed.error ||
          parsed.details ||
          fallbackMessage
        );
      } catch {
        return text;
      }
    }

    if (typeof responseData === 'string') {
      return responseData;
    }

    return (
      responseData?.message ||
      responseData?.error ||
      error?.message ||
      fallbackMessage
    );
  } catch {
    return error?.message || fallbackMessage;
  }
}

/**
 * Extract a filename from the Content-Disposition response header.
 */
function getResponseFilename(contentDisposition, fallbackFilename) {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  const utf8Match = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i
  );

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/["']/g, ''));
    } catch {
      return utf8Match[1].replace(/["']/g, '');
    }
  }

  const normalMatch = contentDisposition.match(
    /filename="?([^"]+)"?/i
  );

  if (normalMatch?.[1]) {
    return normalMatch[1].trim().replace(/["']/g, '');
  }

  return fallbackFilename;
}

/**
 * Download an order waybill using the existing backend endpoint.
 *
 * This function is reusable by:
 * - Seller Orders
 * - Admin Orders
 * - Any future order details page
 */
export async function downloadOrderWaybill(orderId) {
  if (
    orderId === undefined ||
    orderId === null ||
    String(orderId).trim() === ''
  ) {
    throw new Error('A valid order ID is required');
  }

  const normalizedOrderId = String(orderId).trim();
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('Your login session has expired. Please login again.');
  }

  try {
    const response = await api.get(
      `/orders/${encodeURIComponent(normalizedOrderId)}/pdf`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const contentType =
      response.headers?.['content-type'] || 'application/pdf';

    /*
     * Protect against a backend returning JSON or HTML with HTTP 200
     * instead of returning a real PDF.
     */
    if (
      contentType &&
      !contentType.toLowerCase().includes('application/pdf')
    ) {
      const unexpectedResponse = new Blob([response.data], {
        type: contentType,
      });

      const responseText = await unexpectedResponse.text();

      try {
        const parsed = JSON.parse(responseText);

        throw new Error(
          parsed.message ||
            parsed.error ||
            'The server did not return a valid PDF'
        );
      } catch (parseError) {
        if (
          parseError instanceof Error &&
          parseError.message !==
            'The server did not return a valid PDF'
        ) {
          throw parseError;
        }

        throw new Error('The server did not return a valid PDF');
      }
    }

    const pdfBlob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data], {
            type: 'application/pdf',
          });

    const fallbackFilename = `waybill-order-${normalizedOrderId}.pdf`;

    const filename = getResponseFilename(
      response.headers?.['content-disposition'],
      fallbackFilename
    );

    const objectUrl = window.URL.createObjectURL(pdfBlob);
    const downloadLink = document.createElement('a');

    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    downloadLink.style.display = 'none';

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();

    /*
     * Revoke after a small delay so slower browsers have enough time
     * to start the download.
     */
    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 1000);

    return {
      filename,
      orderId: normalizedOrderId,
    };
  } catch (error) {
    const message = await getBlobErrorMessage(error);
    throw new Error(message);
  }
}

export default downloadOrderWaybill;